import { AlertTriangle, ChevronRight, Copy, Link2, OctagonAlert, X } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import { Alert } from 'common-src/components/base';
import { getDashboardClients } from 'common-src/models/Client';
import { getPatientIntegrationsAthena } from 'common-src/models/PatientIntegrationsAthena';

import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';

import {
  getGateBadgeLabel,
  pathwayLabel,
  POST_VISIT_BADGE_META_BY_STATUS,
  PRIORITY_BADGE_COPY,
  PRIORITY_SORT_ORDER,
  READINESS_BADGE_META_BY_STATUS,
  TAB_KEYS,
  TASK_PRIORITY,
  TASK_QUEUE_TEAM_TABS,
  VISIT_TYPES,
} from '../../constants';
import {
  athenaUrl,
  buildLinkedTaskStructure,
  buildRenderableSectionItems,
  buildTeamTaskShareText,
  canMarkTaskComplete,
  copyTextToClipboard,
  dayName,
  daysUntil,
  EASTERN_TIMEZONE,
  formatClinicianName,
  formatDue,
  formatRelativeTime,
  getAppointmentReadinessStatus,
  getCareOrchestrationPath,
  getClientByAthenaProviderId,
  getEasternDateString,
  getLinkedTaskWaitingForTitle,
  getPatient,
  getPostVisitCardStatus,
  getPostVisitTaskDaysRolling,
  getReadinessTaskStatusLabel,
  getTaskDeepLinkUrl,
  getTaskDisplayTitle,
  getTaskReadinessStatus,
  isLinkedTask,
  isTaskBlockedStatus,
  isTaskCanceled,
  isTaskCanceledForReadiness,
  isTaskEscalatedStatus,
  isTaskReadinessComplete,
  isWorkflowBlockedTask,
  monthDay,
} from '../../helpers';
import AthenaLink from '../AthenaLink';
import { Badge } from '../Badge';
import { HuddleNotesSummary } from '../HuddleNotes';
import { MocaDecision, MocaOutcomeLine, useMocaPrompt } from '../MocaPrompt';
import PlatformLink from '../PlatformLink';
import ProviderAvatars from '../ProviderAvatars';
import {
  BriefRow,
  ClinicLocalTime,
  clinicVisitTimes,
  FactSection,
  PanelHeadline,
  ProgressLine,
  ReadinessSquares,
  visitCode,
  visitLabelWithoutCode,
  VisitSections,
} from '../Visit';
import styles from './ReadinessDetail.module.scss';
import { getTaskTypeIcon } from './taskTypeIcons';
import { groupTasksByTeam } from './teamGrouping';
import TeamSectionGroup from './TeamSectionGroup';

const getTaskTone = (task) => {
  if (isTaskCanceledForReadiness(task)) return 'canceled';
  if (isTaskReadinessComplete(task)) return 'done';
  if (task?.priority === 'critical' || isTaskBlockedStatus(task)) {
    return 'critical';
  }
  return 'active';
};

const ReadinessTaskRow = ({
  task,
  onSelectTask,
  waitingForTaskTitle,
  isLinkedChild,
  isSubtaskChild,
  linkedChildCount,
  subtaskChildCount,
  linkedExpanded,
  onToggleLinked,
  completedLabel,
}) => {
  const [isPriorityBadgePulsing, setIsPriorityBadgePulsing] = React.useState(false);
  const { showAlert, AlertType } = useAlert();
  const taskTone = getTaskTone(task);
  const due = formatDue(task?.due);
  const displayStatusLabel = getReadinessTaskStatusLabel(task);
  const claimBy = task?.taskAssignDueAt ? formatDue(task.taskAssignDueAt) : null;
  const isCompleted = isTaskReadinessComplete(task);
  const isCanceledForReadiness = isTaskCanceledForReadiness(task);
  const isBlocked = isTaskBlockedStatus(task) && !isCompleted;
  const isEscalated = isTaskEscalatedStatus(task) && !isCompleted;
  const isPrerequisiteBlockedTask = isLinkedTask(task);
  const isBlockedByDependencies = isWorkflowBlockedTask(task);
  const isHiddenTask = !canMarkTaskComplete(task);
  const TaskTypeIcon = getTaskTypeIcon(task);
  const taskPriority = task?.priority ?? TASK_PRIORITY.Low;
  const taskTitle = getTaskDisplayTitle(task);
  const taskDescription = `${task?.taskDescription || task?.task_description || ''}`.trim();
  const showPriorityBorder = !isCompleted;
  const priorityBorderClass = isPrerequisiteBlockedTask
    ? styles.taskRowPriorityLinked
    : styles[`taskRowPriority${taskPriority}`] || '';
  const showPriorityBadge = !isCompleted;
  const showCriticalIcon =
    !isPrerequisiteBlockedTask &&
    !isBlocked &&
    showPriorityBorder &&
    taskPriority === TASK_PRIORITY.Critical;
  const taskDeepLinkUrl = getTaskDeepLinkUrl(task);
  const totalChildCount = linkedChildCount + subtaskChildCount;
  const showLinkedToggle = totalChildCount > 0 && typeof onToggleLinked === 'function';
  const childToggleLabel = [
    linkedChildCount > 0
      ? `${linkedChildCount} linked ${linkedChildCount === 1 ? 'task' : 'tasks'}`
      : null,
    subtaskChildCount > 0
      ? `${subtaskChildCount} sub-${subtaskChildCount === 1 ? 'task' : 'tasks'}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  React.useEffect(() => {
    if (taskPriority !== TASK_PRIORITY.Critical || isCompleted) {
      setIsPriorityBadgePulsing(false);
      return undefined;
    }

    setIsPriorityBadgePulsing(true);
    const timer = setTimeout(() => setIsPriorityBadgePulsing(false), 1000);
    return () => clearTimeout(timer);
  }, [isCompleted, taskPriority, task?.id]);

  const handleCopyTaskLink = async (event) => {
    event.stopPropagation();
    if (!taskDeepLinkUrl) return;

    try {
      const didCopy = await copyTextToClipboard(taskDeepLinkUrl);
      if (!didCopy) throw new Error('Copy failed');
      showAlert(AlertType.Success, '', 'Task link copied to clipboard.');
    } catch (error) {
      showAlert(AlertType.Error, '', 'Unable to copy task link.');
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={`${styles.taskRowWrap} ${styles[`taskRow${taskTone}`] || ''} ${
        showPriorityBorder ? priorityBorderClass : ''
      } ${!isCompleted && isHiddenTask && !isPrerequisiteBlockedTask ? styles.taskRowDisabled : ''} ${
        isLinkedChild || isSubtaskChild ? styles.taskRowChild : ''
      }`}
      onClick={() => onSelectTask?.(task)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelectTask?.(task);
        }
      }}
      title={
        !isCompleted && isHiddenTask && !isPrerequisiteBlockedTask
          ? 'Task can be claimed, but cannot be completed yet'
          : undefined
      }
    >
      {isPrerequisiteBlockedTask ? (
        <span className={styles.prerequisiteIcon} aria-hidden="true">
          <Link2 className={styles.prerequisiteIconGlyph} />
        </span>
      ) : showCriticalIcon ? (
        <span className={styles.criticalIcon} aria-hidden="true">
          <AlertTriangle className={styles.criticalIconGlyph} strokeWidth={2.2} />
        </span>
      ) : isCompleted || isCanceledForReadiness ? (
        <span
          className={`${styles.taskCheck} ${isCompleted ? styles.taskCheckDone : ''} ${
            isCanceledForReadiness ? styles.taskCheckCanceled : ''
          }`}
          aria-hidden="true"
        >
          {isCanceledForReadiness ? '-' : '✓'}
        </span>
      ) : TaskTypeIcon ? (
        <span className={styles.taskTypeIcon} aria-hidden="true">
          <TaskTypeIcon className={styles.taskTypeIconGlyph} strokeWidth={2} />
        </span>
      ) : null}
      <span className={styles.taskContent}>
        <span className={styles.taskMainRow}>
          <span className={styles.taskTitleWrap}>
            <span className={styles.taskTitleRow}>
              <span className={styles.taskTitle}>{taskTitle}</span>
              {isBlocked ? <Badge variant="critical">Blocked</Badge> : null}
              {!isBlocked && isEscalated ? <Badge variant="escalated">Escalated</Badge> : null}
            </span>
          </span>
          <span className={styles.taskRightRail}>
            <span
              className={`${styles.taskDue} ${isCompleted ? styles.taskDueDone : ''} ${
                isCanceledForReadiness ? styles.taskDueCanceled : ''
              } ${due.urgent ? styles.taskDueUrgent : ''}`}
            >
              {isCompleted ? displayStatusLabel : due.text}
            </span>
            {showPriorityBadge ? (
              <>
                <span
                  className={`${styles.priorityBadge} ${
                    styles[`priorityBadge${taskPriority}`] || ''
                  } ${isPriorityBadgePulsing ? styles.priorityBadgePulse : ''}`}
                >
                  {PRIORITY_BADGE_COPY[taskPriority] ?? taskPriority}
                </span>
                <button
                  type="button"
                  className={styles.copyButton}
                  onClick={handleCopyTaskLink}
                  disabled={!taskDeepLinkUrl}
                  aria-label="Copy task link"
                  title="Copy task link"
                >
                  <Copy className={styles.copyIcon} strokeWidth={1.8} />
                </button>
              </>
            ) : null}
          </span>
        </span>
        {taskDescription ? <span className={styles.taskDescription}>{taskDescription}</span> : null}
        {isCompleted && completedLabel ? (
          <span className={styles.completedByLabel}>{completedLabel}</span>
        ) : null}
        {isPrerequisiteBlockedTask && waitingForTaskTitle ? (
          <span className={styles.waitingForRow}>
            <span className={styles.waitingForIcon} aria-hidden="true">
              <Link2 className={styles.waitingForIconGlyph} />
            </span>
            <span className={styles.waitingForLabel}>
              {isBlockedByDependencies ? 'Blocked by:' : 'Waiting on:'}
            </span>
            <span className={styles.waitingForTitle}>{waitingForTaskTitle}</span>
          </span>
        ) : null}
        {showLinkedToggle ? (
          <button
            type="button"
            className={styles.linkedToggle}
            onClick={(event) => {
              event.stopPropagation();
              onToggleLinked?.(task.id);
            }}
          >
            <ChevronRight
              className={`${styles.linkedToggleChevron} ${
                linkedExpanded ? styles.linkedToggleChevronOpen : ''
              }`}
              strokeWidth={1.8}
            />
            <Link2 className={styles.linkedToggleIcon} strokeWidth={1.8} />
            <span>{childToggleLabel}</span>
          </button>
        ) : null}
        {!isCompleted && claimBy ? (
          <span className={styles.taskCaption}>Assign by: {claimBy.text}</span>
        ) : null}
        {task.caption ? <span className={styles.taskCaption}>{task.caption}</span> : null}
      </span>
    </div>
  );
};

ReadinessTaskRow.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    assessmentType: PropTypes.string,
    caption: PropTypes.string,
    taskDescription: PropTypes.string,
    task_description: PropTypes.string,
    due: PropTypes.string,
    taskAssignDueAt: PropTypes.string,
    status: PropTypes.number,
    priority: PropTypes.string,
    escalatedStatus: PropTypes.number,
    isVisible: PropTypes.bool,
    dependency: PropTypes.object,
  }),
  onSelectTask: PropTypes.func,
  waitingForTaskTitle: PropTypes.string,
  isLinkedChild: PropTypes.bool,
  isSubtaskChild: PropTypes.bool,
  linkedChildCount: PropTypes.number,
  subtaskChildCount: PropTypes.number,
  linkedExpanded: PropTypes.bool,
  onToggleLinked: PropTypes.func,
  completedLabel: PropTypes.string,
};

const getAppointmentPathway = (appointmentTasks = [], patientPathway = null) => {
  const pathwayCounts = appointmentTasks.reduce((acc, task) => {
    const pathway = task?.pathway;
    if (!pathway) return acc;
    acc[pathway] = (acc[pathway] ?? 0) + 1;
    return acc;
  }, {});

  const topPathway = Object.entries(pathwayCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
  return topPathway ?? patientPathway ?? null;
};

const TEAM_ORDER = TASK_QUEUE_TEAM_TABS.filter((tab) => tab.key !== 'all').map((tab) => tab.key);

const formatVisitAgoLabel = (days) => {
  if (!Number.isFinite(days)) return null;
  if (days === 0) return 'today';
  if (days === -1) return '1d ago';
  if (days < 0) return `${Math.abs(days)}d ago`;
  return `in ${days}d`;
};

const ReadinessDetail = ({
  appt,
  onClose,
  onSelectTask,
  appointmentTasks = [],
  delegatedSubtasksByParentId = {},
  isPostVisit = false,
  departmentLabelById = {},
  onDismiss,
  pinnedTeam = null,
  onTogglePinnedTeam,
  sectionRequest = null,
  huddleNotes = [],
  onOpenHuddleNotes,
}) => {
  const { showAlert, AlertType } = useAlert();
  if (!appt) return null;
  const [expandedLinkedTasks, setExpandedLinkedTasks] = React.useState({});
  const moca = useMocaPrompt(appt, 'Prepare');
  const rootRef = React.useRef(null);
  React.useEffect(() => {
    if (!sectionRequest || sectionRequest.apptId !== appt?.id) return;
    rootRef.current
      ?.querySelector(`[data-section="${sectionRequest.section}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [sectionRequest, appt?.id]);
  const [expandedTeams, setExpandedTeams] = React.useState({});

  React.useEffect(() => {
    setExpandedTeams({});
  }, [pinnedTeam]);

  const sourceAppointmentId = appt?.sourceAppointmentId ?? appt?.id;
  const patient = appt?.patient ?? getPatient(appt?.patientId);
  const visitType = VISIT_TYPES[appt.visitType];
  const workflowDueDateOnly = getEasternDateString(appt?.workflowDueAt);
  const days = workflowDueDateOnly ? daysUntil(workflowDueDateOnly, EASTERN_TIMEZONE) : null;
  const readinessTasks = React.useMemo(() => {
    const tasks = appointmentTasks.filter(Boolean);

    return [...tasks].sort((a, b) => {
      const aCompleted = isTaskReadinessComplete(a);
      const bCompleted = isTaskReadinessComplete(b);
      if (aCompleted !== bCompleted) return aCompleted ? -1 : 1;

      if (!aCompleted && !bCompleted) {
        const aLinked = isLinkedTask(a);
        const bLinked = isLinkedTask(b);
        if (aLinked !== bLinked) return aLinked ? 1 : -1;
      }

      const priorityDiff =
        (PRIORITY_SORT_ORDER[a?.priority] ?? Number.POSITIVE_INFINITY) -
        (PRIORITY_SORT_ORDER[b?.priority] ?? Number.POSITIVE_INFINITY);
      if (priorityDiff !== 0) return priorityDiff;

      const aDue = a?.due ? new Date(a.due).getTime() : Number.POSITIVE_INFINITY;
      const bDue = b?.due ? new Date(b.due).getTime() : Number.POSITIVE_INFINITY;
      if (aDue !== bDue) return aDue - bDue;

      return `${a?.title || ''}`.localeCompare(`${b?.title || ''}`);
    });
  }, [appointmentTasks]);
  const activeReadinessTasks = React.useMemo(
    () => readinessTasks.filter((task) => !isTaskCanceledForReadiness(task)),
    [readinessTasks],
  );
  const canceledReadinessTasks = React.useMemo(
    () => readinessTasks.filter((task) => isTaskCanceledForReadiness(task)),
    [readinessTasks],
  );
  const linkedTaskStructure = React.useMemo(
    () => buildLinkedTaskStructure(activeReadinessTasks, sourceAppointmentId),
    [activeReadinessTasks, sourceAppointmentId],
  );
  const delegatedSubtaskCountByParentId = React.useMemo(
    () =>
      activeReadinessTasks.reduce((acc, readinessTask) => {
        const parentId = `${readinessTask?.id || ''}`.trim();
        if (!parentId) return acc;

        const uniqueSubtasks = new Map();
        const localSubtasks = linkedTaskStructure.subtaskChildTasksByParentId.get(parentId) || [];
        const hydratedSubtasks = delegatedSubtasksByParentId[parentId] || [];

        [...hydratedSubtasks, ...localSubtasks].forEach((subtask) => {
          const subtaskId = `${subtask?.id || ''}`.trim();
          if (!subtaskId || isTaskCanceled(subtask)) return;
          uniqueSubtasks.set(subtaskId, subtask);
        });

        acc[parentId] = uniqueSubtasks.size;
        return acc;
      }, {}),
    [activeReadinessTasks, delegatedSubtasksByParentId, linkedTaskStructure],
  );
  const completedTasks = readinessTasks.filter((task) => isTaskReadinessComplete(task));
  const readinessStatus = getAppointmentReadinessStatus(activeReadinessTasks);
  const readinessBadgeMeta = READINESS_BADGE_META_BY_STATUS[readinessStatus] ?? null;
  const gateBadgeLabel = getGateBadgeLabel(days);
  const postVisitStatus = isPostVisit ? getPostVisitCardStatus(activeReadinessTasks) : null;
  const postVisitBadgeMeta = POST_VISIT_BADGE_META_BY_STATUS[postVisitStatus] ?? null;
  const postVisitOpenTasks = activeReadinessTasks.filter((task) => !isTaskReadinessComplete(task));
  const postVisitRollingCount = isPostVisit ? postVisitOpenTasks.length : 0;
  const postVisitOldestRollingDays = postVisitRollingCount
    ? Math.max(...postVisitOpenTasks.map(getPostVisitTaskDaysRolling))
    : 0;
  const visitDate = appt?.departmentLocalDate || appt?.date;
  const visitTimes = clinicVisitTimes(appt);
  const visitAgoLabel = isPostVisit
    ? formatVisitAgoLabel(visitDate ? daysUntil(visitDate, appt?.departmentTimezone) : NaN)
    : null;
  const integrations = useCustomSelector((state) =>
    patient?.id ? getPatientIntegrationsAthena(state, patient.id) : [],
  );
  const athenaIntegration = integrations[0] ?? null;
  const clients = useCustomSelector((state) => getDashboardClients(state)) || [];
  const athenaLink = athenaUrl(athenaIntegration?.athenaPatientId, athenaIntegration?.practiceId);
  const athenaPatientId = athenaIntegration?.athenaPatientId || patient?.athenaId || '-';
  const platformMrn = patient?.mrn ?? patient?.id ?? appt?.patientId ?? null;
  const visitUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}${getCareOrchestrationPath(
    TAB_KEYS.READINESS,
  )}`;
  const visitLinkUrl = `${visitUrl}?visit=${encodeURIComponent(`${appt?.id ?? ''}`)}`;
  const clinicianClient = getClientByAthenaProviderId(clients, appt?.providerAthenaId);
  const clinician = formatClinicianName(clinicianClient);
  const pathway = getAppointmentPathway(readinessTasks, patient?.pathway);
  const location = departmentLabelById[`${appt?.departmentAthenaId ?? ''}`] || appt.location || '-';
  const waitingForTaskTitleById = React.useMemo(
    () =>
      readinessTasks.reduce((acc, task) => {
        if (task?.isVisible !== false) return acc;

        acc[task.id] = getLinkedTaskWaitingForTitle(task, readinessTasks, sourceAppointmentId);
        return acc;
      }, {}),
    [readinessTasks, sourceAppointmentId],
  );
  const toggleLinkedTasks = React.useCallback((taskId) => {
    const normalizedTaskId = `${taskId || ''}`;
    if (!normalizedTaskId) return;

    setExpandedLinkedTasks((prev) => ({
      ...prev,
      [normalizedTaskId]: !prev[normalizedTaskId],
    }));
  }, []);
  const toggleTeam = React.useCallback((teamKey) => {
    setExpandedTeams((prev) => ({
      ...prev,
      [teamKey]: !(prev[teamKey] ?? true),
    }));
  }, []);
  const getClientNameById = React.useCallback(
    (clientId) => {
      const normalizedClientId = Number(clientId);
      if (!Number.isInteger(normalizedClientId)) return null;

      const client = clients.find((candidateClient) => candidateClient?.id === normalizedClientId);
      if (!client) return null;

      if (typeof client.getName === 'function') return client.getName(false, true);

      return `${client?.firstName || ''} ${client?.lastName || ''}`.trim() || null;
    },
    [clients],
  );
  const getCompletedLabel = React.useCallback(
    (task) => {
      if (!isTaskReadinessComplete(task)) return null;

      const name = getClientNameById(task?.updatedBy);
      if (!name) return null;

      const ago = formatRelativeTime(task?.updatedAt);
      return `Done by ${name}${ago ? ` · ${ago}` : ''}`;
    },
    [getClientNameById],
  );
  const renderReadinessRow = React.useCallback(
    (task) => (
      <ReadinessTaskRow
        key={task.id}
        task={task}
        onSelectTask={onSelectTask}
        waitingForTaskTitle={waitingForTaskTitleById[task.id] ?? null}
        isLinkedChild={linkedTaskStructure.linkedChildIds.has(`${task.id}`)}
        isSubtaskChild={linkedTaskStructure.subtaskChildIds.has(`${task.id}`)}
        completedLabel={getCompletedLabel(task)}
        isSubtask={!!task?.parentTaskId}
        parentTitle={
          task?.parentTaskId
            ? (activeReadinessTasks.find(
                (candidateTask) => `${candidateTask?.id || ''}` === `${task.parentTaskId || ''}`,
              )?.title ?? null)
            : null
        }
        linkedChildCount={linkedTaskStructure.parentLinkedChildCount.get(`${task.id}`) ?? 0}
        subtaskChildCount={Math.max(
          isTaskReadinessComplete(task) ? 0 : 0,
          delegatedSubtaskCountByParentId[`${task.id}`] ?? 0,
        )}
        linkedExpanded={!!expandedLinkedTasks[`${task.id}`]}
        onToggleLinked={toggleLinkedTasks}
      />
    ),
    [
      activeReadinessTasks,
      delegatedSubtaskCountByParentId,
      expandedLinkedTasks,
      getCompletedLabel,
      linkedTaskStructure,
      onSelectTask,
      toggleLinkedTasks,
      waitingForTaskTitleById,
    ],
  );
  const renderReadinessItems = React.useCallback(
    (items) =>
      items.map((item) => {
        if (item.type === 'child-group') {
          return (
            <div key={item.key} className={styles.childGroupContainer}>
              {item.items.map((childItem) => {
                if (childItem.type === 'label') {
                  return (
                    <div key={childItem.key} className={styles.childSectionLabel}>
                      {childItem.label}
                    </div>
                  );
                }

                if (childItem.type === 'child-group') {
                  return renderReadinessItems([childItem])[0];
                }

                return renderReadinessRow(childItem.task);
              })}
            </div>
          );
        }

        return renderReadinessRow(item.task);
      }),
    [renderReadinessRow],
  );
  const teamGroups = React.useMemo(() => {
    const groups = groupTasksByTeam(activeReadinessTasks, TEAM_ORDER, pinnedTeam);

    return groups.map((group) => {
      const openTasks = group.tasks.filter((task) => !isTaskReadinessComplete(task));
      const completedGroupTasks = group.tasks.filter((task) => isTaskReadinessComplete(task));

      return {
        ...group,
        openTasks,
        completedTasks: completedGroupTasks,
        openItems: buildRenderableSectionItems(
          openTasks,
          linkedTaskStructure,
          expandedLinkedTasks,
          delegatedSubtasksByParentId,
        ),
        completedItems: buildRenderableSectionItems(
          completedGroupTasks,
          linkedTaskStructure,
          expandedLinkedTasks,
          delegatedSubtasksByParentId,
        ),
        openCount: openTasks.length,
        completedCount: completedGroupTasks.length,
        totalCount: group.tasks.length,
      };
    });
  }, [
    activeReadinessTasks,
    delegatedSubtasksByParentId,
    expandedLinkedTasks,
    linkedTaskStructure,
    pinnedTeam,
  ]);
  const buildCopyTextForGroup = React.useCallback(
    (group) =>
      buildTeamTaskShareText({
        patientName: patient?.name,
        visitLabel: visitType?.label,
        visitDate,
        platformMrn,
        athenaId: athenaPatientId,
        teamLabel: group.label,
        openTasks: group.openTasks.map((task) => ({
          title: getTaskDisplayTitle(task),
          dueText: formatDue(task?.due).text,
          priority: task?.priority,
          owner: task?.owner,
          url: getTaskDeepLinkUrl(task),
          blockedByTitle: waitingForTaskTitleById[task.id] ?? null,
        })),
        completedTasks: group.completedTasks.map((task) => ({
          title: getTaskDisplayTitle(task),
          completedBy: getCompletedLabel(task)?.replace(/^Done by /, '') ?? null,
        })),
        visitUrl,
      }),
    [
      athenaPatientId,
      getCompletedLabel,
      patient?.name,
      platformMrn,
      visitDate,
      visitType?.label,
      visitUrl,
      waitingForTaskTitleById,
    ],
  );

  const handleCopyVisitLink = async () => {
    try {
      const didCopy = await copyTextToClipboard(visitLinkUrl);
      if (!didCopy) throw new Error('Copy failed');
      showAlert(AlertType.Success, '', 'Visit link copied to clipboard.');
    } catch (error) {
      showAlert(AlertType.Error, '', 'Unable to copy visit link.');
    }
  };

  return (
    <div className={styles.root} ref={rootRef}>
      <header>
        <PanelHeadline
          visit={{
            code: visitCode({ cptCode: appt?.cptCode, visitType: appt?.visitType }),
            kindName: appt?.visitType,
            label: visitLabelWithoutCode(appt?.visitType),
          }}
          title={patient?.name}
          qualifier={isPostVisit ? 'Completed visit' : undefined}
          actions={
            <>
              <button
                type="button"
                onClick={handleCopyVisitLink}
                className={styles.closeButton}
                aria-label="Copy visit link"
                title="Copy visit link"
              >
                <Copy size={14} strokeWidth={1.8} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={onClose}
                className={styles.closeButton}
                aria-label="Close"
              >
                <X size={14} strokeWidth={1.8} aria-hidden="true" />
              </button>
            </>
          }
        />
        <VisitSections>
          <FactSection label="Member">
            {(pathwayLabel[pathway] ?? pathway ?? patient?.phase) && (
              <span className={styles.memberLine}>
                {[pathwayLabel[pathway] ?? pathway, patient?.phase && `Phase ${patient.phase}`]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            )}
            <div className={styles.linkRow}>
              {(patient || appt?.patientId) && (
                <PlatformLink line patient={patient} patientId={appt?.patientId} />
              )}
              {patient && <AthenaLink line patient={patient} integration={athenaIntegration} />}
              {platformMrn != null && <span className={styles.mrn}>MRN-{platformMrn}</span>}
            </div>
          </FactSection>
          <FactSection label="Care team">
            {clinicianClient ? (
              <ProviderAvatars name={clinician} jobTitle="Provider" />
            ) : (
              <span className={styles.quiet}>No clinician on file</span>
            )}
          </FactSection>
          <FactSection label="Booking">
            <span className={styles.bookingLine}>
              {[
                `${dayName(visitTimes.date)}, ${monthDay(visitTimes.date)}`,
                visitTimes.range,
                visitAgoLabel,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
            <span className={styles.bookingMeta}>
              {[location !== '-' && location, `${visitTimes.minutes} min booked`]
                .filter(Boolean)
                .join(' · ')}
            </span>
            <ClinicLocalTime compact appt={appt} />
          </FactSection>
          <FactSection label={isPostVisit ? 'Follow-up' : 'Readiness'}>
            <span className={styles.statusRow}>
              {isPostVisit ? (
                postVisitBadgeMeta ? (
                  <Badge variant={postVisitBadgeMeta.variant} className={styles.panelBadge}>
                    {postVisitBadgeMeta.label}
                  </Badge>
                ) : null
              ) : (
                <>
                  {readinessBadgeMeta ? (
                    <Badge variant={readinessBadgeMeta.variant} className={styles.panelBadge}>
                      {readinessBadgeMeta.label}
                    </Badge>
                  ) : null}
                  {gateBadgeLabel ? (
                    <span className={styles.gateLabel}>{gateBadgeLabel}</span>
                  ) : null}
                </>
              )}
            </span>
            {isPostVisit ? (
              <ProgressLine
                done={completedTasks.length}
                total={readinessTasks.length}
                status={postVisitStatus}
              />
            ) : (
              <ReadinessSquares
                blocks={readinessTasks.map((task) => ({
                  id: task.id,
                  status: getTaskReadinessStatus(task),
                }))}
                done={completedTasks.length}
                total={readinessTasks.length}
              />
            )}
          </FactSection>
        </VisitSections>
        {isPostVisit && postVisitStatus === 'red' && (
          <div className={styles.stalledAlert}>
            <Alert
              variant="destructive"
              role="status"
              icon={OctagonAlert}
              title="Follow-up stalled"
            >
              {`${
                completedTasks.length === 0
                  ? 'No follow-up task done yet'
                  : `Oldest task open ${postVisitOldestRollingDays} ${
                      postVisitOldestRollingDays === 1 ? 'day' : 'days'
                    }`
              } \u00b7 ${postVisitRollingCount} open`}
            </Alert>
          </div>
        )}
      </header>

      {isPostVisit && appt?.lowMocaPrompt && (
        <div className={styles.mocaFlag} data-section="post-visit-moca">
          <MocaOutcomeLine appt={appt} />
        </div>
      )}
      {!isPostVisit && appt?.lowMocaPrompt && (
        <div className={styles.mocaFlag} data-section="readiness-moca">
          <MocaDecision
            latestMoca={appt.latestMoca}
            moca={moca}
            hideWhenNotNeeded
            inset={false}
            timeZone={appt.departmentTimezone}
            day={appt.departmentLocalDate || appt.date}
          />
        </div>
      )}

      <div className={styles.topics}>
        <div data-section="readiness-notes">
          <BriefRow
            label="Huddle notes"
            flush
            meta={
              huddleNotes.length
                ? `${huddleNotes.length} ${huddleNotes.length === 1 ? 'note' : 'notes'}`
                : 'Add a note'
            }
          >
            <HuddleNotesSummary heading={false} notes={huddleNotes} onOpen={onOpenHuddleNotes} />
          </BriefRow>
        </div>

        <div data-section="readiness-tasks">
          <BriefRow
            label="Tasks"
            flush
            meta={
              readinessTasks.length === 0 ? undefined : isPostVisit ? (
                <span className={styles.tasksMeta}>
                  <span
                    className={postVisitOpenTasks.length === 0 ? styles.tasksMetaDone : undefined}
                  >
                    {postVisitOpenTasks.length === 0
                      ? 'All done'
                      : `${postVisitOpenTasks.length} open`}
                  </span>
                  {completedTasks.length > 0 && postVisitOpenTasks.length > 0 ? (
                    <>
                      <span className={styles.tasksMetaDot} aria-hidden="true">
                        ·
                      </span>
                      <span
                        className={styles.tasksMetaDone}
                      >{`${completedTasks.length} done`}</span>
                    </>
                  ) : null}
                </span>
              ) : (
                <ProgressLine
                  done={completedTasks.length}
                  total={readinessTasks.length}
                  status={readinessStatus === 'default' ? 'neutral' : readinessStatus}
                  className={styles.tasksProgress}
                />
              )
            }
          >
            {readinessTasks.length > 0 ? (
              <div className={styles.teamGroupsWrap}>
                {teamGroups.map((group) => (
                  <TeamSectionGroup
                    key={group.team}
                    team={group.team}
                    label={group.label}
                    openItems={group.openItems}
                    completedItems={group.completedItems}
                    openCount={group.openCount}
                    completedCount={group.completedCount}
                    totalCount={group.totalCount}
                    expanded={
                      expandedTeams[group.team] ?? (pinnedTeam ? group.team === pinnedTeam : true)
                    }
                    onToggleExpanded={toggleTeam}
                    renderItems={renderReadinessItems}
                    getCopyText={() => buildCopyTextForGroup(group)}
                    isDefault={!!pinnedTeam && group.team === pinnedTeam}
                    onToggleDefault={onTogglePinnedTeam}
                  />
                ))}
                {canceledReadinessTasks.length > 0 ? (
                  <div className={styles.canceledTasksSection}>
                    {canceledReadinessTasks.map((task) => (
                      <ReadinessTaskRow
                        key={task.id}
                        task={task}
                        onSelectTask={onSelectTask}
                        waitingForTaskTitle={waitingForTaskTitleById[task.id] ?? null}
                        isLinkedChild={false}
                        isSubtaskChild={false}
                        linkedChildCount={0}
                        subtaskChildCount={0}
                        linkedExpanded={false}
                        onToggleLinked={toggleLinkedTasks}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className={styles.emptyTasks}>No tasks for this appointment.</div>
            )}
          </BriefRow>
        </div>
      </div>

      <div className={styles.sectionActions}>
        {isPostVisit ? (
          onDismiss ? (
            <div className={styles.actionsRow}>
              <button
                type="button"
                className={styles.hideButton}
                onClick={() => onDismiss(appt?.id)}
              >
                Hide from post-visit view
              </button>
            </div>
          ) : null
        ) : (
          <>
            <div className={styles.actionsRow}>
              <a
                href={athenaLink || undefined}
                target={athenaLink ? '_blank' : undefined}
                rel={athenaLink ? 'noopener noreferrer' : undefined}
                className={`${styles.rescheduleLink} ${athenaLink ? '' : styles.disabledLink}`}
                aria-disabled={!athenaLink}
                onClick={(e) => {
                  if (!athenaLink) e.preventDefault();
                }}
              >
                Reschedule in Athena ↗
              </a>
            </div>
            {readinessStatus === 'red' && days != null && days <= 2 && (
              <div className={styles.finalCheck}>
                <Alert variant="destructive" icon={OctagonAlert} title="Final check">
                  This visit has unresolved blockers within 48 hours. Consider rescheduling or
                  escalating to the clinician.
                </Alert>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

ReadinessDetail.propTypes = {
  huddleNotes: PropTypes.array,
  onOpenHuddleNotes: PropTypes.func,
  appt: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sourceAppointmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lowMocaPrompt: PropTypes.bool,
    patient: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      pathway: PropTypes.string,
      phase: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    visitType: PropTypes.string,
    cptCode: PropTypes.string,
    workflowDueAt: PropTypes.string,
    date: PropTypes.string,
    departmentLocalDate: PropTypes.string,
    departmentTimezone: PropTypes.string,
    time: PropTypes.string,
    departmentAthenaId: PropTypes.string,
    providerAthenaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    location: PropTypes.string,
    latestMoca: PropTypes.shape({ score: PropTypes.number, maxScore: PropTypes.number }),
  }),
  onClose: PropTypes.func,
  onSelectTask: PropTypes.func,
  appointmentTasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      assessmentType: PropTypes.string,
      caption: PropTypes.string,
      due: PropTypes.string,
      status: PropTypes.number,
      priority: PropTypes.string,
      isVisible: PropTypes.bool,
    }),
  ),
  delegatedSubtasksByParentId: PropTypes.object,
  isPostVisit: PropTypes.bool,
  departmentLabelById: PropTypes.object,
  onDismiss: PropTypes.func,
  pinnedTeam: PropTypes.string,
  onTogglePinnedTeam: PropTypes.func,
  sectionRequest: PropTypes.shape({
    apptId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    section: PropTypes.string,
  }),
};

export default ReadinessDetail;
